// T339 — a lazy import that fails honestly after a deploy.
//
// Vite content-hashes every chunk, so each build renames them. A browser that
// loaded the page before a deploy is holding an `index.js` whose `import()`
// still names the OLD hash, and that file no longer exists — the request 404s
// and the promise rejects with a message about a "dynamically imported module".
//
// Live on 2026-09-24: the operator clicked Download PDF and got
//
//   PDF export failed: Failed to fetch dynamically imported module:
//   .../assets/jspdf.es.min-CM9LVeOZ.js
//
// while the server held `jspdf.es.min-F2BZPlBh.js`. Nothing was broken; his tab
// was simply older than the build. But the message named a filename he has no
// way to act on and called it an export failure, which it is not.
//
// This is not a rare condition here: the frontend image is rebuilt from scratch
// on every deploy, so the previous chunks are gone the moment it restarts, and
// a tab left open across one deploy is enough.

const STALE_PATTERNS = [
  /dynamically imported module/i,     // Chrome, Edge
  /error loading dynamically imported/i, // Firefox
  /Importing a module script failed/i, // Safari
  /Failed to fetch/i,                 // a bare network failure on the chunk
];

/** Does this rejection look like a chunk that the running build no longer has? */
export function isStaleChunkError(err) {
  const msg = String(err?.message ?? err ?? '');
  return STALE_PATTERNS.some((re) => re.test(msg));
}

export class StaleBuildError extends Error {
  constructor(what, cause) {
    super(
      `${what} could not load because the app was updated since this tab opened. `
      + 'Reload the page and try again.',
    );
    this.name = 'StaleBuildError';
    this.cause = cause;
    this.stale = true;
  }
}

/**
 * Run a dynamic import, converting the stale-chunk rejection into something the
 * caller can present as an action rather than as a filename.
 *
 * Deliberately no auto-reload. Losing an unsaved edit to get a PDF is a worse
 * trade than one extra click, and this modal holds text the operator may have
 * been editing.
 *
 * @param {() => Promise<any>} loader  e.g. `() => import('jspdf')`
 * @param {string} what                what the operator was trying to do
 */
export async function lazyImport(loader, what = 'That feature') {
  try {
    return await loader();
  } catch (err) {
    if (isStaleChunkError(err)) throw new StaleBuildError(what, err);
    throw err;
  }
}
